import React, { useEffect, useState } from 'react';
import BBALoader from './BBALoader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleUserIntegrationCallback } from '../services/userIntegrationService';
import { handleIntegrationCallbackClient } from '../services/integrationService';
import { useAuth } from '../contexts/AuthContext';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

console.log('🟢 IntegrationCallback component loaded - version 2.1');

const checkIfCrmUser = async (userId) => {
  try {
    const docSnap = await getDoc(doc(db, 'internal_users', userId));
    return docSnap.exists();
  } catch {
    return false;
  }
};

// Integrations stored at account level (internalIntegrations/{accountId})
const ACCOUNT_LEVEL_INTEGRATIONS = [
  'quickbooks', 'stripe', 'twilio', 'zapier', 'meta', 'docusign',
  'activecampaign', 'googleanalytics', 'youtube', 'googlemaps', 'gdrive',
];

// Google OAuth integrations that all share /auth/google/callback
const GOOGLE_REDIRECT_INTEGRATIONS = [
  'googleCalendar', 'gmail', 'gdrive', 'googleanalytics', 'youtube', 'googlemaps',
];

/**
 * Parse the OAuth state param. Formats:
 *   "integrationKey"                         — legacy
 *   "integrationKey:userId"                  — user-level
 *   "integrationKey|encodedRedirectUri"      — QuickBooks / DocuSign
 *   "integrationKey|accountId|encodedRedUri" — googleanalytics portal connect
 */
function parseState(state) {
  if (!state) return {};

  if (state.includes('|')) {
    const parts = state.split('|');
    const integrationKey = parts[0];
    if (parts.length >= 3) {
      // "googleanalytics|accountId|encodedRedirectUri"
      return {
        integrationKey,
        embeddedAccountId: parts[1],
        redirectUriFromState: decodeURIComponent(parts[2]),
      };
    }
    // "quickbooks|encodedRedirectUri" or similar two-part
    return {
      integrationKey,
      redirectUriFromState: decodeURIComponent(parts[1]),
    };
  }

  if (state.includes(':')) {
    const [integrationKey, userId] = state.split(':');
    return { integrationKey, userId };
  }

  return { integrationKey: state };
}

const IntegrationCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing integration...');

  useEffect(() => {
    let isProcessing = false;
    let authCheckIntervalId = null;

    const portalProfilePath = '/portal/profile';
    const crmProfilePath = '/crm/my-profile';

    const handleCallback = async () => {
      if (isProcessing) return;
      isProcessing = true;

      if (!auth.currentUser) {
        let attempts = 0;
        authCheckIntervalId = setInterval(() => {
          attempts++;
          if (auth.currentUser) {
            clearInterval(authCheckIntervalId);
            authCheckIntervalId = null;
            isProcessing = false;
            handleCallback();
          } else if (attempts >= 12) {
            clearInterval(authCheckIntervalId);
            authCheckIntervalId = null;
            isProcessing = false;
            setStatus('error');
            setMessage('Please log in to complete the integration');
            setTimeout(() => navigate('/login'), 3000);
          }
        }, 500);
        return;
      }

      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const oauthError = searchParams.get('error');

        const isCrmUser = auth.currentUser
          ? await checkIfCrmUser(auth.currentUser.uid)
          : false;
        const fallbackPath = isCrmUser ? crmProfilePath : portalProfilePath;

        if (oauthError) {
          setStatus('error');
          setMessage(`Integration failed: ${oauthError}`);
          setTimeout(() => navigate(fallbackPath), 3000);
          return;
        }

        if (!code || !state) {
          setStatus('error');
          setMessage('Missing required parameters');
          setTimeout(() => navigate(fallbackPath), 3000);
          return;
        }

        const {
          integrationKey,
          userId: stateUserId,
          redirectUriFromState,
          embeddedAccountId,
        } = parseState(state);

        if (!integrationKey) {
          setStatus('error');
          setMessage('Invalid state parameter');
          setTimeout(() => navigate(fallbackPath), 3000);
          return;
        }

        // Validate user match for user-level state format
        if (stateUserId && currentUser && stateUserId !== currentUser.uid) {
          setStatus('error');
          setMessage('User ID mismatch');
          setTimeout(() => navigate(fallbackPath), 3000);
          return;
        }

        const isAccountLevel = ACCOUNT_LEVEL_INTEGRATIONS.includes(integrationKey);
        const userId = stateUserId || currentUser?.uid;

        if (!isAccountLevel && !userId) {
          setStatus('error');
          setMessage('User not authenticated');
          setTimeout(() => navigate(fallbackPath), 3000);
          return;
        }

        setMessage(`Connecting ${integrationKey}...`);

        const realmId = searchParams.get('realmId');

        // Resolve accountId for account-level integrations
        let accountId = embeddedAccountId || null;
        if (isAccountLevel && !accountId && auth.currentUser) {
          try {
            const portalUserDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (portalUserDoc.exists()) {
              accountId = portalUserDoc.data().accountId || null;
            }
            if (!accountId) {
              const internalUserDoc = await getDoc(doc(db, 'internal_users', auth.currentUser.uid));
              if (internalUserDoc.exists()) {
                accountId = 'dotware';
              }
            }
          } catch (err) {
            console.warn('Could not fetch accountId:', err);
          }
        }

        if (!accountId && isAccountLevel) {
          setStatus('error');
          setMessage('Missing account ID');
          setTimeout(() => navigate(fallbackPath), 3000);
          return;
        }

        const redirectUri = redirectUriFromState || (
          GOOGLE_REDIRECT_INTEGRATIONS.includes(integrationKey)
            ? `${window.location.origin}/auth/google/callback`
            : `${window.location.origin}/auth/${integrationKey}/callback`
        );

        console.log('🟢 IntegrationCallback: Processing', {
          integrationKey, isAccountLevel, accountId: accountId || 'none', redirectUri,
        });

        let result;
        if (isAccountLevel) {
          result = await handleIntegrationCallbackClient({ code, state, accountId, realmId, redirectUri });
        } else {
          const userRedirectUri = GOOGLE_REDIRECT_INTEGRATIONS.includes(integrationKey)
            ? `${window.location.origin}/auth/google/callback`
            : `${window.location.origin}/auth/${integrationKey}/callback`;
          result = await handleUserIntegrationCallback(integrationKey, code, userId, userRedirectUri);
        }

        if (result.success) {
          setStatus('success');
          setMessage(`${integrationKey} connected successfully!`);
          let returnPath;
          if (integrationKey === 'googleanalytics' && isCrmUser) {
            // CRM users land on the Lead Portal report after connecting Google Analytics
            returnPath = '/crm/lead-portal-reports';
          } else {
            returnPath = isCrmUser ? crmProfilePath : portalProfilePath;
          }
          setTimeout(() => navigate(returnPath, { state: { refreshIntegrations: true } }), 2000);
        } else {
          setStatus('error');
          setMessage(`Failed to connect ${integrationKey}: ${result.error}`);
          setTimeout(() => navigate(fallbackPath), 3000);
        }
      } catch (error) {
        console.error('Error handling integration callback:', error);
        setStatus('error');
        setMessage(`Error: ${error.message}`);
        const isCrmUser = auth.currentUser
          ? await checkIfCrmUser(auth.currentUser.uid)
          : false;
        setTimeout(() => navigate(isCrmUser ? crmProfilePath : portalProfilePath), 3000);
      } finally {
        isProcessing = false;
      }
    };

    if (searchParams.get('code') || searchParams.get('error')) {
      handleCallback();
    }

    return () => {
      if (authCheckIntervalId) clearInterval(authCheckIntervalId);
    };
  }, [searchParams, navigate, currentUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          {status === 'processing' && (
            <BBALoader size={48} className="mx-auto mb-4" />
          )}
          {status === 'success' && (
            <div className="text-green-600 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          {status === 'error' && (
            <div className="text-red-600 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}
          <h2 className={`text-xl font-semibold mb-2 ${
            status === 'success' ? 'text-green-600' :
            status === 'error' ? 'text-red-600' : 'text-gray-900'
          }`}>
            {status === 'success' ? 'Success!' : status === 'error' ? 'Error' : 'Processing'}
          </h2>
          <p className="text-gray-600 mb-4">{message}</p>
          <button
            onClick={async () => {
              const isCrmUser = auth.currentUser
                ? await checkIfCrmUser(auth.currentUser.uid)
                : false;
              navigate(isCrmUser ? '/crm/my-profile' : '/portal/profile');
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Return to Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationCallback;
